import React, { useState, useEffect } from 'react';
import GoogleCalendarApi from '../api/GoogleCalendarApi';

const GoogleCalendarSync = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('googleAccessToken');
    if (token) {
      setIsAuthenticated(true);
      fetchUserInfo();
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await GoogleCalendarApi.getUserInfo();
      setUserInfo(response.user);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const handleGoogleSync = async () => {
    try {
      setLoading(true);
      setError(null);

      const authResponse = await GoogleCalendarApi.getAuthUrl();
      const authUrl = authResponse.authUrl;

      const popup = window.open(
        authUrl,
        'googleAuth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setLoading(false);
        }
      }, 1000);


      const messageListener = (event) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          const { tokens } = event.data;

          localStorage.setItem('googleAccessToken', tokens.access_token);
          if (tokens.refresh_token) {
            localStorage.setItem('googleRefreshToken', tokens.refresh_token);
          }

          setIsAuthenticated(true);
          setLoading(false);
          popup.close();
          window.removeEventListener('message', messageListener);


          fetchUserInfo();
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          setError(event.data.error);
          setLoading(false);
          popup.close();
          window.removeEventListener('message', messageListener);
        }
      };

      window.addEventListener('message', messageListener);

    } catch (error) {
      console.error('Error starting Google sync:', error);
      setError('Failed to start Google authentication');
      setLoading(false);
    }
  };

  const handleSyncEvents = async () => {
    try {
      setLoading(true);
      setError(null);


      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);

      const events = await GoogleCalendarApi.getCalendarEvents(
        'primary',
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );

      setEvents(events);
      setLoading(false);
    } catch (error) {
      console.error('Error syncing events:', error);
      setError('Failed to sync events');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('googleAccessToken');
    localStorage.removeItem('googleRefreshToken');
    setIsAuthenticated(false);
    setUserInfo(null);
    setEvents([]);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>Google Calendar Sync</h2>

      {error && (
        <div style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {!isAuthenticated ? (
        <div>
          <p>Connect your Google Calendar to sync events.</p>
          <button
            onClick={handleGoogleSync}
            disabled={loading}
            style={{
              backgroundColor: '#4285f4',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {loading ? 'Connecting...' : 'Connect Google Calendar'}
          </button>
        </div>
      ) : (
        <div>
          <div style={{
            backgroundColor: '#e8f5e8',
            padding: '15px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <h3>✅ Connected to Google Calendar</h3>
            {userInfo && (
              <p>
                <strong>Email:</strong> {userInfo.email}<br />
                <strong>Name:</strong> {userInfo.name}
              </p>
            )}
            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Disconnect
            </button>
          </div>

          <div>
            <button
              onClick={handleSyncEvents}
              disabled={loading}
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                marginBottom: '20px'
              }}
            >
              {loading ? 'Syncing...' : 'Sync Events'}
            </button>

            {events.length > 0 && (
              <div>
                <h3>Synced Events ({events.length})</h3>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {events.map((event, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid #ddd',
                        padding: '15px',
                        marginBottom: '10px',
                        borderRadius: '4px',
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <h4>{event.summary || 'No Title'}</h4>
                      {event.description && (
                        <p><strong>Description:</strong> {event.description}</p>
                      )}
                      {event.location && (
                        <p><strong>Location:</strong> {event.location}</p>
                      )}
                      {event.start && (
                        <p>
                          <strong>Start:</strong> {new Date(event.start.dateTime || event.start.date).toLocaleString()}
                        </p>
                      )}
                      {event.end && (
                        <p>
                          <strong>End:</strong> {new Date(event.end.dateTime || event.end.date).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarSync;
